// ─── DateTimePicker ───────────────────────────────────────────────────────────
// XD-style date + time picker used by the chart Start Date / End Date controls.
// Replaces the native <input type="date"> (which can't show a time field) with a
// popup that matches the XD design: a month calendar, a "Time" input, and
// Now / Done buttons.
//
// Value contract (same as the chart date range elsewhere in the app):
//   value:    'YYYY-MM-DD HH:MM:SS'  (or 'YYYY-MM-DD', or '' when unset)
//   onChange: called with a full 'YYYY-MM-DD HH:MM:SS' string
//
// `defaultTime` ('HH:MM:SS') seeds the time when the incoming value has none —
// '00:00:00' for a start date, '23:59:59' for an end date — preserving the old
// behaviour until the user edits the time.
// ──────────────────────────────────────────────────────────────────────────────
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isValid,
} from 'date-fns'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

const pad = (n) => String(n).padStart(2, '0')

// Parse 'YYYY-MM-DD[ HH:MM:SS]' into { date: Date|null, time: 'HH:MM:SS' }.
function parseValue(value, defaultTime) {
  const safeDefault = /^\d{2}:\d{2}:\d{2}$/.test(defaultTime || '') ? defaultTime : '00:00:00'
  if (!value || typeof value !== 'string') return { date: null, time: safeDefault }
  const [datePart, timePart] = value.trim().split(/[ T]/)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart || '')
  if (!m) return { date: null, time: safeDefault }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (!isValid(d)) return { date: null, time: safeDefault }
  let time = safeDefault
  if (timePart) {
    const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(timePart)
    if (t) time = `${pad(t[1])}:${pad(t[2])}:${pad(t[3] || '00')}`
  }
  return { date: d, time }
}

const fmtTrigger = (date) => (date ? `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}` : '')

function composeValue(date, time) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${time}`
}

export default function DateTimePicker({
  value,
  onChange,
  defaultTime = '00:00:00',
  className = '',
  placeholder = 'mm/dd/yyyy',
  ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  const { date: selectedDate, time } = useMemo(() => parseValue(value, defaultTime), [value, defaultTime])
  const [viewMonth, setViewMonth] = useState(() => selectedDate || new Date())

  // When opening, jump the calendar to the selected month.
  useEffect(() => {
    if (open) setViewMonth(selectedDate || new Date())
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 })
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 })
    return eachDayOfInterval({ start, end })
  }, [viewMonth])

  const emit = (date, t) => onChange?.(composeValue(date, t))

  const pickDay = (day) => emit(day, time)

  const changeTime = (hhmm) => {
    if (!/^\d{2}:\d{2}$/.test(hhmm)) return
    const base = selectedDate || new Date()
    emit(base, `${hhmm}:00`)
  }

  const setNow = () => {
    const now = new Date()
    emit(now, `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`)
    setViewMonth(now)
  }

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <input
          type="text"
          readOnly
          aria-label={ariaLabel}
          value={fmtTrigger(selectedDate)}
          placeholder={placeholder}
          onClick={() => setOpen((v) => !v)}
          className={(className || 'common-size15 border-b text-[#4D4D4D] border-gray-200 focus:outline-none focus:border-[#358ECD] bg-white') + ' w-full pr-5 cursor-pointer'}
        />
        {/* Calendar glyph (matches the old native date input / XD) */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open calendar"
          className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#358ECD]"
          tabIndex={-1}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>

      {open && (
        <div className="absolute z-[10000] mt-1 w-[260px] rounded-xl border border-gray-200 bg-white shadow-2xl p-3 text-[#4D4D4D]"
             role="dialog">
          {/* Month header */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setViewMonth((m) => subMonths(m, 1))}
                    className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="Previous month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <span className="text-sm font-semibold">{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
            <button type="button" onClick={() => setViewMonth((m) => addMonths(m, 1))}
                    className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="Next month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>

          {/* Weekday row */}
          <div className="grid grid-cols-7 text-center text-[11px] text-gray-400 mb-1">
            {WEEKDAYS.map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 text-center text-[13px]">
            {gridDays.map((day) => {
              const inMonth = isSameMonth(day, viewMonth)
              const isSelected = selectedDate && isSameDay(day, selectedDate)
              const isToday = isSameDay(day, new Date())
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => pickDay(day)}
                  className={[
                    'h-8 w-8 mx-auto my-0.5 rounded-full leading-8 transition',
                    isSelected ? 'bg-[#358ECD] text-white font-semibold'
                      : inMonth ? 'text-[#4D4D4D] hover:bg-gray-100' : 'text-gray-300 hover:bg-gray-50',
                    !isSelected && isToday ? 'ring-1 ring-[#358ECD]' : '',
                  ].join(' ')}
                >
                  {day.getDate()}
                </button>
              )
            })}
          </div>

          <div className="my-2 border-t border-gray-100" />

          {/* Time row */}
          <div className="flex items-center gap-2 mb-2">
            <label className="text-[13px] text-gray-600 w-10">Time</label>
            <input
              type="time"
              value={time.slice(0, 5)}
              onChange={(e) => changeTime(e.target.value)}
              className="flex-1 border border-gray-200 rounded px-2 py-1 text-[13px] text-[#4D4D4D] focus:outline-none focus:border-[#358ECD]"
            />
          </div>

          {/* Now / Done */}
          <div className="flex items-center justify-between">
            <button type="button" onClick={setNow}
                    className="text-[13px] text-[#358ECD] px-3 py-1 rounded hover:bg-gray-50">Now</button>
            <button type="button" onClick={() => setOpen(false)}
                    className="text-[13px] text-white bg-[#358ECD] px-4 py-1.5 rounded hover:opacity-90">Done</button>
          </div>
        </div>
      )}
    </div>
  )
}
